import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notifyError } from '../../../utils/notify';
import { getFeed, getTrendingFeed } from '../../../store/slices/feed/asyncThunks';
import { selectGetFeedInfo, selectGetTrendingInfo } from '../../../store/slices/feed/slice';
import { DEFAULT_FEED_TAB, FEED_LIMIT, FEED_PHASE, FEED_TABS } from '../constants';
import { getEntityId, mapFeedItemsToReels } from '../../../utils/feed';

const useReelsFeed = (targetReelId, activeTab = DEFAULT_FEED_TAB) => {
  const dispatch = useDispatch();
  const loadMoreRef = useRef(null);
  const isTrending = activeTab === FEED_TABS.TRENDING;

  const newInfo = useSelector(selectGetFeedInfo);
  const trendingInfo = useSelector(selectGetTrendingInfo);
  const info = isTrending ? trendingInfo : newInfo;

  const { data, pending, fetchMorePending, hasMore, loaded } = info;

  // New paginates by cursor; Trending relies on hasMore (cursor may be null).
  const hasNextPage = isTrending ? true : Boolean(newInfo.nextCursor);

  const loadMore = useCallback(() => {
    if (isTrending) {
      const loadedTrendingIds = data
        .filter(item => item?.isTrending)
        .map(getEntityId)
        .filter(Boolean);

      return dispatch(
        getTrendingFeed({
          limit: FEED_LIMIT,
          phase: trendingInfo.phase || FEED_PHASE.TRENDING,
          cursor: trendingInfo.cursor,
          seed: trendingInfo.seed,
          excludedFeedItems: loadedTrendingIds,
        }),
      );
    }

    return dispatch(getFeed({ limit: FEED_LIMIT, cursor: newInfo.nextCursor }));
  }, [data, dispatch, isTrending, newInfo.nextCursor, trendingInfo.cursor, trendingInfo.phase, trendingInfo.seed]);

  useEffect(() => {
    if (loaded || pending || data.length) {
      return;
    }

    const request = isTrending
      ? dispatch(getTrendingFeed({ limit: FEED_LIMIT, phase: FEED_PHASE.TRENDING }))
      : dispatch(getFeed({ limit: FEED_LIMIT }));

    request.unwrap().catch(error => {
      notifyError(error.message || 'Unable to load reels right now.');
    });
  }, [data.length, dispatch, isTrending, loaded, pending]);

  useEffect(() => {
    const hasTargetReel = targetReelId ? data.some(item => getEntityId(item) === targetReelId) : false;

    if (!targetReelId || hasTargetReel || !loaded || pending || fetchMorePending || !hasMore || !hasNextPage) {
      return;
    }

    loadMore()
      .unwrap()
      .catch(error => {
        notifyError(error.message || 'Unable to load the shared reel right now.');
      });
  }, [data, fetchMorePending, hasMore, hasNextPage, loadMore, loaded, pending, targetReelId]);

  const handleIntersection = useCallback(
    entries => {
      const [entry] = entries;

      if (entry.isIntersecting && !pending && !fetchMorePending && hasMore && hasNextPage) {
        loadMore()
          .unwrap()
          .catch(error => {
            notifyError(error.message || 'Unable to load more reels right now.');
          });
      }
    },
    [fetchMorePending, hasMore, hasNextPage, loadMore, pending],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      threshold: 0.4,
    });
    const target = loadMoreRef.current;

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [handleIntersection]);

  const reels = useMemo(() => mapFeedItemsToReels(data), [data]);

  return {
    reels,
    pending,
    fetchMorePending,
    hasMore,
    loaded,
    loadMoreRef,
  };
};

export default useReelsFeed;
